import ResultCard from "@/components/calculators/ResultCard"
import PageHero from "@/components/PageHero"
import SEOHead from "@/components/SEOHead"
import { formatPercent, formatRatio, safeDivide, toNumber } from "@/lib/format"
import {Info} from 'lucide-react'
import { useState } from "react"

function BreakEvenRoas() {
  const [margin, setMargin] = useState("50")
  const [showTip, setShowTip] = useState(false)
  const marginNum = Math.min(100, Math.max(0, toNumber(margin)))

  const breakEvenRoas = safeDivide(1, marginNum / 100)
  const breakEvenRoasPercent = breakEvenRoas * 100

  return (
    <div>
      <SEOHead
        title="Break-Even ROAS Calculator"
        description="Find the minimum ROAS your campaign needs to reach based on product gross margin, and protect your profits from rising ad costs."
      />

      <PageHero
        eyebrow="Margin floor"
        title="Break-Even ROAS Calculator"
        description="Calculate the minimum ROAS needed to prevent campaign losses based on your product's gross margin."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 pb-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-7">
          <div className="flex items-center gap-1.5 mb-3">
            <label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">Product Gross Margin %</label>
            <span
              className="relative inline-flex"
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
            >
              <button
                type="button"
                onClick={() => setShowTip((prev) => !prev)}
                aria-label="More info about Product Gross Margin"
                className="text-slate-400 hover:text-emerald-600 transition-colors"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
              {showTip ? (
                <span className="absolute z-20 left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 rounded-lg bg-slate-900 text-white text-xs leading-relaxed px-3 py-2 shadow-lg">
                  Gross margin = (Revenue - Cost of Goods Sold) / Revenue x 100. This is the profit cushion left before ad spend.
                  <span className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-slate-900 rotate-45" />
                </span>
              ) : null}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={1}
              max={100}
              value={marginNum}
              onChange={(e) => setMargin(e.target.value)}
              className="w-full accent-emerald-600"
            />
            <div className="relative w-24 flex-shrink-0">
              <input
                type="text"
                inputMode="decimal"
                value={margin}
                onChange={(e) => setMargin(e.target.value.replace(/[^0-9.]/g, ""))}
                className="w-full rounded-lg border border-slate-200 bg-slate-50/80 py-2.5 pl-3 pr-7 font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 focus:bg-white"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">%</span>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ResultCard label="Break-Even ROAS (Ratio)" value={formatRatio(breakEvenRoas)} emphasis formula="1 / (Margin / 100)" />
            <ResultCard label="Break-Even ROAS (%)" value={formatPercent(breakEvenRoasPercent)} formula="(1 / (Margin / 100)) x 100" />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-4">How to Find Your Product Gross Margin</h2>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
          Gross margin is calculated as <span className="font-semibold text-slate-800">(Revenue - Cost of Goods Sold) / Revenue</span>.
          A $50 product costing $25 to produce and deliver has a 50% gross margin. This is the profit cushion left
          over before advertising costs are subtracted.
        </p>
        <p className="text-[15px] text-slate-600 leading-relaxed">
          Your break-even ROAS tells you the exact point where ad spend consumes all of that margin. Any ROAS below
          this threshold means you are losing money on every sale after accounting for product costs — even if
          the campaign appears "profitable" on a pure revenue basis. Use this number as a hard floor when setting
          bidding targets, especially as CPCs and CPMs continue to rise across major ad platforms.
        </p>
      </section>
    </div>
  )
}

export default BreakEvenRoas
